import type { DeleteLedgerAccountMutationOptions } from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { UUID } from '~/types/common';

import { queryKeysLedgerAccount } from '../queryKeys';

import { deleteLedgerAccount } from './deleteLedgerAccount';

/**
 * Custom hook for soft-deleting a ledger account's currently open version.
 *
 * @param options - Optional mutation options.
 * @returns An object containing the mutation function and related properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Ledger-Accounts
 */
export const useMutationDeleteLedgerAccount = (
  options?: DeleteLedgerAccountMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ledgerAccountId: UUID) =>
      deleteLedgerAccount(ledgerAccountId),
    ...options,
    onSuccess(data, ledgerAccountId, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysLedgerAccount.get(ledgerAccountId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysLedgerAccount.getAll(),
      });

      return options?.onSuccess?.(
        data,
        ledgerAccountId,
        onMutateResult,
        context,
      );
    },
  });
};
